import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Fuel, Plus, Trash2, Save, AlertTriangle, CheckCircle, Loader2, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI } from '../../utils/api';

export default function DGDigitisationEditor({ siteId }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSummary, setShowSummary] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logsRes, summaryRes] = await Promise.all([
        prospectsAPI.getSiteDGLogs(siteId),
        prospectsAPI.getDGMonthlySummary(siteId),
      ]);
      const logs = logsRes.data || [];
      const digitised = logs.filter(l => l.is_digitised);
      if (digitised.length > 0) {
        setRows(digitised.map(l => ({
          dg_log_id: l.dg_log_id,
          date: l.date || '',
          runtime_hours: l.runtime_hours || '',
          diesel_liters: l.diesel_liters || '',
          remarks: l.remarks || '',
          is_outlier: l.is_outlier || false,
          parsing_status: l.parsing_status || 'NEEDS_REVIEW',
          source: l.source || 'manual',
        })));
      }
      setSummary(summaryRes.data || []);
    } catch (e) { console.error('DGDigitisationEditor load failed', e); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addRow = () => {
    setRows(prev => [...prev, { dg_log_id: '', date: '', runtime_hours: '', diesel_liters: '', remarks: '', is_outlier: false }]);
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      // Check outlier
      const runtime = Number(updated.runtime_hours) || 0;
      const diesel = Number(updated.diesel_liters) || 0;
      if (runtime > 0 && diesel > 0) {
        const lph = diesel / runtime;
        updated.is_outlier = lph > 15 || lph < 1;
      } else {
        updated.is_outlier = false;
      }
      return updated;
    }));
  };

  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const valid = rows.filter(r => r.date || r.runtime_hours || r.diesel_liters);
    if (valid.length === 0) { toast.error('Add at least one row'); return; }
    setSaving(true);
    try {
      const payload = valid.map(r => ({
        dg_log_id: r.dg_log_id || undefined,
        date: r.date,
        runtime_hours: r.runtime_hours ? Number(r.runtime_hours) : 0,
        diesel_liters: r.diesel_liters ? Number(r.diesel_liters) : 0,
        remarks: r.remarks,
      }));
      const res = await prospectsAPI.batchSaveDGRows(siteId, payload);
      toast.success(`Saved ${res.data.saved} rows${res.data.outliers?.length ? `, ${res.data.outliers.length} outliers flagged` : ''}`);
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="dg-digitisation-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Fuel className="w-4 h-4 text-slate-500" />
          <p className="text-xs font-semibold text-slate-600 uppercase">DG Log Digitisation</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowSummary(!showSummary)} data-testid="toggle-summary">
            <BarChart3 className="w-3 h-3 mr-1" /> {showSummary ? 'Hide' : 'Show'} Summary
          </Button>
          <Button size="sm" variant="outline" className="text-xs h-7" onClick={addRow} data-testid="add-dg-row">
            <Plus className="w-3 h-3 mr-1" /> Add Row
          </Button>
          <Button size="sm" className="bg-slate-900 text-xs h-7" onClick={handleSave} disabled={saving} data-testid="save-dg-rows">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save All
          </Button>
        </div>
      </div>

      {/* Monthly Summary */}
      {showSummary && summary.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Monthly Summary</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="dg-monthly-summary">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-1 px-2 font-medium text-slate-500">Month</th>
                  <th className="text-right py-1 px-2 font-medium text-slate-500">Runtime (hrs)</th>
                  <th className="text-right py-1 px-2 font-medium text-slate-500">Diesel (L)</th>
                  <th className="text-right py-1 px-2 font-medium text-slate-500">Avg L/hr</th>
                  <th className="text-right py-1 px-2 font-medium text-slate-500">Rows</th>
                  <th className="text-right py-1 px-2 font-medium text-slate-500">Outliers</th>
                </tr>
              </thead>
              <tbody>
                {summary.map(s => (
                  <tr key={s.month} className="border-b border-slate-100">
                    <td className="py-1 px-2 font-medium">{s.month}</td>
                    <td className="py-1 px-2 text-right">{s.total_runtime_hours}</td>
                    <td className="py-1 px-2 text-right">{s.total_diesel_liters}</td>
                    <td className="py-1 px-2 text-right">{s.avg_lph}</td>
                    <td className="py-1 px-2 text-right">{s.row_count}</td>
                    <td className="py-1 px-2 text-right">{s.outlier_count > 0 ? <span className="text-red-500 font-medium">{s.outlier_count}</span> : '0'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Table Editor */}
      {rows.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-sm p-6 text-center">
          <Fuel className="w-6 h-6 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No DG log entries</p>
          <p className="text-xs text-slate-300 mt-1">Click "Add Row" to start digitising handwritten DG logs</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-2 px-3 font-medium text-slate-500 w-32">Date</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500 w-28">Runtime (hrs)</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500 w-28">Diesel (L)</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Remarks</th>
                <th className="text-center py-2 px-3 font-medium text-slate-500 w-16">Status</th>
                <th className="text-center py-2 px-3 font-medium text-slate-500 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className={`border-b border-slate-50 ${row.is_outlier ? 'bg-red-50' : ''}`} data-testid={`dg-row-${idx}`}>
                  <td className="py-1.5 px-3"><Input type="date" value={row.date} onChange={e => updateRow(idx, 'date', e.target.value)} className="h-7 text-xs" /></td>
                  <td className="py-1.5 px-3"><Input type="number" step="0.5" value={row.runtime_hours} onChange={e => updateRow(idx, 'runtime_hours', e.target.value)} className="h-7 text-xs" /></td>
                  <td className="py-1.5 px-3"><Input type="number" step="0.5" value={row.diesel_liters} onChange={e => updateRow(idx, 'diesel_liters', e.target.value)} className="h-7 text-xs" /></td>
                  <td className="py-1.5 px-3"><Input value={row.remarks} onChange={e => updateRow(idx, 'remarks', e.target.value)} className="h-7 text-xs" placeholder="..." /></td>
                  <td className="py-1.5 px-3 text-center">
                    {row.is_outlier ? (
                      <span className="text-red-500" title="Outlier: L/hr outside 1-15 range"><AlertTriangle className="w-3.5 h-3.5 inline" /></span>
                    ) : row.date ? (
                      <span className="text-emerald-500"><CheckCircle className="w-3.5 h-3.5 inline" /></span>
                    ) : null}
                    {row.source === 'auto_parser' && (
                      <span className="ml-1 text-[9px] text-indigo-500 uppercase font-semibold" title="Auto-parsed from upload">auto</span>
                    )}
                  </td>
                  <td className="py-1.5 px-3 text-center">
                    <button onClick={() => removeRow(idx)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
